'use strict';

const libPictProvider = require('pict-provider');

/**
 * DocumentThroughput — a REUSABLE document-approval throughput dashboard. The metric it visualizes is universal across the
 * platform: how many documents of a given type are submitted and approved over time (plus how long approval takes), broken
 * down by configurable dimensions. Inspection dossiers, structural-concrete reports, anything with an approval workflow.
 *
 * It is driven by a per-type config (see [[DocumentThroughput-Configs.js]]): `{ DocumentType, Title, Dimensions:[…] }`.
 * The Inspection Schedule points it at 'Inspection-Dossier'; a future Structural-Concrete screen points it at that report
 * type with Dimensions of Producer + Project — no code change, just a config entry.
 *
 * Data (plain Meadow, no server work, and — crucially — no slow full-Document scans):
 *   1. `Documents/Lite/FilteredTo/…DocumentType~EQ~<type>…` — fast, FormData-free; yields IDDocument + IDProject + Name.
 *   2. `DocumentApprovalLogs/FilteredTo/FBL~IDDocument~INN~<chunk>~FBV~CreateDate~GE~<windowStart>…` — the dated approval
 *      transitions (Action 'Submit' → submitted, IsApproval=1 → approved); log queries are well-indexed and ~80ms.
 * Everything else is aggregated client-side. The inspection's real status lives in this approval workflow, NOT the
 * Inspection.StatusDisplay shell.
 */

const _SUB = '#2f6f9f';   // submitted
const _APP = '#3f9e6a';   // approved
const _MON = [ 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec' ];
const _DOC_CAP = 4000;

class DocumentThroughputProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
		this._mount = '#DocumentThroughput-Host';
		this._config = null;            // the active per-type config
		this._trailing = 12;
		this._docMeta = {};             // IDDocument -> { IDProject, Name }
		this._docType = '';             // type the current _docMeta was loaded for
		this._names = { user: {}, project: {} };
		this._agg = null;               // computed aggregation
		this._loading = false;
		this._error = '';
		this._loadGen = 0;
	}

	get restClient() { return (this.pict.fable && this.pict.fable.HeadlightRestClient) || this.pict.HeadlightRestClient; }
	get idCustomer() { return (this.pict.AppData && this.pict.AppData.CurrentUser && this.pict.AppData.CurrentUser.CustomerID) || 0; }
	get providerName() { return 'DocumentThroughput'; }
	_get(pURL) { return new Promise((resolve) => this.restClient.getJSON(pURL, (e, b) => resolve(e ? null : b))); }
	_esc(pV) { return String(pV == null ? '' : pV).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }

	// Configure for a document type. `pConfig` is a DocumentThroughput config object.
	configure(pConfig, pMount) { this._config = pConfig || null; if (pMount) { this._mount = pMount; } return this; }

	// --- date helpers ---
	_monthKeys()
	{
		const out = []; const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0);
		for (let i = this._trailing - 1; i >= 0; i--) { const m = new Date(d.getFullYear(), d.getMonth() - i, 1); out.push({ key: `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, '0')}`, y: m.getFullYear(), m: m.getMonth() }); }
		return out;
	}
	_windowStartYMD() { const d = new Date(); const s = new Date(d.getFullYear(), d.getMonth() - (this._trailing - 1), 1); return `${s.getFullYear()}-${String(s.getMonth() + 1).padStart(2, '0')}-01`; }

	async loadAsync()
	{
		if (!this._config) { return; }
		const gen = ++this._loadGen;
		this._loading = true; this._error = ''; this._paint();
		try
		{
			await this._loadLiteDocs(gen);
			if (gen !== this._loadGen) { return; }
			const logs = await this._loadLogs(gen);
			if (gen !== this._loadGen) { return; }
			await this._aggregate(logs, gen);
		}
		catch (e) { this._error = (e && e.message) || String(e); }
		finally { if (gen === this._loadGen) { this._loading = false; this._paint(); } }
	}

	async _loadLiteDocs(pGen)
	{
		const type = this._config.DocumentType;
		if (this._docType === type && Object.keys(this._docMeta).length) { return; } // cached per type
		// Newest-first (FSF DESC): a trailing-window metric must keep the most RECENT documents when the type exceeds the cap.
		const rows = await this._get(`Documents/Lite/FilteredTo/FBV~IDCustomer~EQ~${this.idCustomer}~FBV~DocumentType~EQ~${encodeURIComponent(type)}~FBV~Deleted~EQ~0~FSF~IDDocument~DESC~0/0/${_DOC_CAP}`);
		if (pGen !== this._loadGen) { return; }
		const meta = {};
		(Array.isArray(rows) ? rows : []).forEach((d) => { meta[d.IDDocument] = { IDProject: d.IDProject || 0, Name: d.Value || `Document ${d.IDDocument}` }; });
		this._docMeta = meta; this._docType = type;
		this._docTruncated = Array.isArray(rows) && rows.length >= _DOC_CAP;
	}

	async _loadLogs(pGen)
	{
		const ids = Object.keys(this._docMeta).map(Number).filter(Boolean);
		if (!ids.length) { return []; }
		const start = this._windowStartYMD();
		const out = [];
		this._logsTruncated = false;
		for (let i = 0; i < ids.length; i += 100)
		{
			const chunk = ids.slice(i, i + 100);
			// Newest-first so a >cap chunk keeps recent months (a trailing-window chart needs the newest, not the oldest).
			const rows = await this._get(`DocumentApprovalLogs/FilteredTo/FBL~IDDocument~INN~${chunk.join(',')}~FBV~CreateDate~GE~${start}~FBV~Deleted~EQ~0~FSF~CreateDate~DESC~0/0/8000`);
			if (pGen !== this._loadGen) { return out; }
			if (Array.isArray(rows)) { out.push(...rows); if (rows.length >= 8000) { this._logsTruncated = true; } }
		}
		return out;
	}

	async _aggregate(pLogs, pGen)
	{
		const months = this._monthKeys(); const idx = {}; months.forEach((mo, i) => { idx[mo.key] = i; });
		// Roll the raw transition log up to ONE record PER DOCUMENT first — a multi-step approval (Engineer→Contractor)
		// writes several IsApproval rows, so counting events would double-count and push the approval rate over 100%.
		const perDoc = {}; // IDDocument -> { firstSubmit, firstApprove, submitter }
		pLogs.forEach((log) =>
		{
			const isSubmit = (log.Action === 'Submit') || (log.AfterStatus === 'Submitted');
			const isApprove = (Number(log.IsApproval) === 1);
			if (!isSubmit && !isApprove) { return; }
			const pd = perDoc[log.IDDocument] || (perDoc[log.IDDocument] = { firstSubmit: null, firstApprove: null, submitter: null });
			if (isSubmit && (!pd.firstSubmit || log.CreateDate < pd.firstSubmit)) { pd.firstSubmit = log.CreateDate; pd.submitter = log.CreatingIDUser; }
			if (isApprove && (!pd.firstApprove || log.CreateDate < pd.firstApprove)) { pd.firstApprove = log.CreateDate; }
		});
		const submitted = new Array(months.length).fill(0); const approved = new Array(months.length).fill(0);
		const dims = (this._config.Dimensions || []).map((d) => ({ def: d, groups: {} }));
		const cycles = [];
		Object.keys(perDoc).forEach((docId) =>
		{
			const pd = perDoc[docId]; const meta = this._docMeta[docId] || {};
			if (pd.firstSubmit) { const mi = idx[pd.firstSubmit.slice(0, 7)]; if (mi != null) { submitted[mi]++; } }
			if (pd.firstApprove) { const mi = idx[pd.firstApprove.slice(0, 7)]; if (mi != null) { approved[mi]++; } }
			if (pd.firstSubmit && pd.firstApprove && pd.firstApprove >= pd.firstSubmit) { cycles.push((new Date(pd.firstApprove) - new Date(pd.firstSubmit)) / 86400000); }
			// dimension tallies — PER DOCUMENT. For a submitter (logField) dimension the key is the doc's SUBMITTER, so its
			// approved column counts that submitter's docs that got approved (not the approver, who is a different person).
			dims.forEach((dim) =>
			{
				const key = dim.def.docField ? meta[dim.def.docField] : (dim.def.logField === 'CreatingIDUser' ? pd.submitter : (dim.def.logField ? pd[dim.def.logField] : null));
				if (key == null || key === 0 || key === '') { return; }
				const g = dim.groups[key] || (dim.groups[key] = { submitted: 0, approved: 0 });
				if (pd.firstSubmit) { g.submitted++; }
				if (pd.firstApprove) { g.approved++; }
			});
		});
		cycles.sort((a, b) => a - b);
		const median = cycles.length ? (cycles.length % 2 ? cycles[(cycles.length - 1) / 2] : (cycles[cycles.length / 2 - 1] + cycles[cycles.length / 2]) / 2) : null;
		await this._resolveDimensionNames(dims, pGen);
		this._agg = {
			months, submitted, approved,
			totalDocs: Object.keys(this._docMeta).length,
			totalSubmitted: submitted.reduce((a, b) => a + b, 0),
			totalApproved: approved.reduce((a, b) => a + b, 0),
			medianCycleDays: median,
			dims,
		};
	}

	async _resolveDimensionNames(pDims, pGen)
	{
		const userIDs = []; const projIDs = [];
		pDims.forEach((dim) => { if (dim.def.resolve === 'user') { Object.keys(dim.groups).forEach((k) => userIDs.push(Number(k))); } else if (dim.def.resolve === 'project') { Object.keys(dim.groups).forEach((k) => projIDs.push(Number(k))); } });
		if (userIDs.length)
		{
			const need = [ ...new Set(userIDs.filter((x) => x && !this._names.user[x])) ];
			for (let i = 0; i < need.length; i += 100)
			{
				const chunk = need.slice(i, i + 100);
				const rows = await this._get(`Users/FilteredTo/FBL~IDUser~INN~${chunk.join(',')}/0/${chunk.length}`);
				if (pGen !== this._loadGen) { return; }
				(Array.isArray(rows) ? rows : []).forEach((u) => { this._names.user[u.IDUser] = [ u.NameFirst, u.NameLast ].filter(Boolean).join(' ') || u.LoginID || `User ${u.IDUser}`; });
			}
		}
		if (projIDs.length)
		{
			const need = [ ...new Set(projIDs.filter((x) => x && !this._names.project[x])) ];
			for (let i = 0; i < need.length; i += 100)
			{
				const chunk = need.slice(i, i + 100);
				const rows = await this._get(`Projects/Lite/FilteredTo/FBL~IDProject~INN~${chunk.join(',')}/0/${chunk.length}`);
				if (pGen !== this._loadGen) { return; }
				(Array.isArray(rows) ? rows : []).forEach((p) => { this._names.project[p.IDProject] = p.Value || p.Name || `Project ${p.IDProject}`; });
			}
		}
	}
	_dimLabel(pDim, pKey)
	{
		if (pDim.def.resolve === 'user') { return this._names.user[pKey] || `User ${pKey}`; }
		if (pDim.def.resolve === 'project') { return this._names.project[pKey] || `Project ${pKey}`; }
		return String(pKey);
	}

	// =========================================================== paint ===========================================================
	renderInto(pMount, pConfig) { this.configure(pConfig, pMount); this.loadAsync(); }
	_paint() { const ca = this.pict.ContentAssignment; if (!ca) { return; } ca.assignContent(this._mount, this._html()); }
	_html()
	{
		if (!this._config) { return ''; }
		if (this._error) { return `${_TP_STYLE}<div class="tp"><div class="tp-empty tp-err"><i class="fas fa-triangle-exclamation"></i> ${this._esc(this._error)}</div></div>`; }
		if (this._loading && !this._agg) { return `${_TP_STYLE}<div class="tp"><div class="tp-empty"><i class="fas fa-circle-notch fa-spin"></i> Loading approval throughput…</div></div>`; }
		const a = this._agg || { months: this._monthKeys(), submitted: [], approved: [], totalDocs: 0, totalSubmitted: 0, totalApproved: 0, medianCycleDays: null, dims: [] };
		const rate = a.totalSubmitted ? Math.min(100, Math.round(100 * a.totalApproved / a.totalSubmitted)) : 0;
		const stats = `<div class="tp-stats">
			${this._stat(a.totalDocs, 'Documents of type', '')}
			${this._stat(a.totalSubmitted, `Submitted · ${this._trailing} mo`, _SUB)}
			${this._stat(a.totalApproved, `Approved · ${this._trailing} mo`, _APP)}
			${this._stat(a.medianCycleDays == null ? '—' : Math.round(a.medianCycleDays) + 'd', 'Median submit → approve', '')}
			${this._stat(rate + '%', 'Approval rate', '')}
		</div>`;
		// Compact embed (e.g. inside the Inspection Schedule Overview): stats + chart only, no dimension tables.
		if (this._config.Compact)
		{
			return `${_TP_STYLE}<div class="tp tp-compact">${stats}
				<div class="tp-card"><div class="tp-card-h">Submitted vs approved per month <span class="tp-legend"><span class="tp-dot" style="background:${_SUB}"></span>submitted <span class="tp-dot" style="background:${_APP}"></span>approved</span></div>${this._chartHTML(a)}</div></div>`;
		}
		return `${_TP_STYLE}<div class="tp">${stats}
			<div class="tp-card"><div class="tp-card-h">Throughput — submitted vs approved per month <span class="tp-legend"><span class="tp-dot" style="background:${_SUB}"></span>submitted <span class="tp-dot" style="background:${_APP}"></span>approved</span></div>${this._chartHTML(a)}</div>
			${(a.dims || []).map((dim) => `<div class="tp-card"><div class="tp-card-h">${this._esc(dim.def.Label || 'Breakdown')}</div>${this._dimHTML(dim)}</div>`).join('')}
			${this._docTruncated ? `<div class="tp-note"><i class="fas fa-circle-info"></i> Showing the first ${_DOC_CAP.toLocaleString()} documents of this type (capped).</div>` : ''}</div>`;
	}
	_stat(v, label, color) { return `<div class="tp-stat"><div class="tp-stat-v"${color ? ` style="color:${color}"` : ''}>${typeof v === 'number' ? v.toLocaleString() : this._esc(v)}</div><div class="tp-stat-l">${this._esc(label)}</div></div>`; }
	_chartHTML(a)
	{
		let max = 1; a.months.forEach((mo, i) => { max = Math.max(max, a.submitted[i] || 0, a.approved[i] || 0); });
		if (!a.totalSubmitted && !a.totalApproved) { return `<div class="tp-empty-sm">No submissions or approvals for this document type in the last ${this._trailing} months.</div>`; }
		const bars = a.months.map((mo, i) =>
		{
			const s = a.submitted[i] || 0; const ap = a.approved[i] || 0;
			return `<div class="tp-bar" title="${_MON[mo.m]} ${mo.y}: ${s} submitted, ${ap} approved">
				<div class="tp-bar-pair">
					<span class="tp-bar-c" style="height:${Math.round(100 * s / max)}%;background:${_SUB}"><span class="tp-bar-n">${s || ''}</span></span>
					<span class="tp-bar-c" style="height:${Math.round(100 * ap / max)}%;background:${_APP}"><span class="tp-bar-n">${ap || ''}</span></span>
				</div>
				<span class="tp-bar-x">${_MON[mo.m]}${mo.m === 0 ? " '" + String(mo.y).slice(2) : ''}</span>
			</div>`;
		}).join('');
		return `<div class="tp-chart">${bars}</div>`;
	}
	_dimHTML(dim)
	{
		const rows = Object.keys(dim.groups).map((k) => ({ key: k, ...dim.groups[k] })).sort((a, b) => b.submitted - a.submitted).slice(0, 12);
		if (!rows.length) { return `<div class="tp-empty-sm">No data.</div>`; }
		const max = Math.max(1, ...rows.map((r) => r.submitted));
		const body = rows.map((r) => `<tr>
			<td class="tp-dim-n">${this._esc(this._dimLabel(dim, r.key))}</td>
			<td class="tp-dim-bar"><div class="tp-dimbar"><span style="width:${Math.round(100 * r.submitted / max)}%;background:${_SUB}"></span></div></td>
			<td class="tp-dim-v">${r.submitted}</td>
			<td class="tp-dim-v" style="color:${_APP}">${r.approved}</td>
		</tr>`).join('');
		return `<table class="tp-dimtable"><thead><tr><th>${this._esc(dim.def.ItemLabel || 'Group')}</th><th></th><th>Subm.</th><th>Appr.</th></tr></thead><tbody>${body}</tbody></table>`;
	}
}

const _TP_STYLE = /*html*/`<style>
	.tp { --b:var(--theme-color-border-light,#e4e9ef); --mut:var(--theme-color-text-muted,#8894a4); --ink:var(--theme-color-text-primary,#1f2733); --sec:var(--theme-color-text-secondary,#45505f); --card:var(--theme-color-background-primary,#fff); --bg2:var(--theme-color-background-secondary,#f4f6f9); font-size:14px; color:var(--ink); }
	.tp-stats { display:grid; grid-template-columns:repeat(auto-fit,minmax(9rem,1fr)); gap:0.7rem; margin:0 0 0.9rem; }
	.tp-stat { background:var(--card); border:1px solid var(--b); border-radius:11px; padding:0.7rem 0.9rem; } .tp-stat-v { font-size:1.5rem; font-weight:800; line-height:1; } .tp-stat-l { font-size:0.68rem; color:var(--mut); margin-top:0.3rem; text-transform:uppercase; letter-spacing:0.03em; font-weight:700; }
	.tp-card { background:var(--card); border:1px solid var(--b); border-radius:12px; padding:0.9rem 1rem; margin:0 0 0.9rem; } .tp-card-h { font-size:0.8rem; font-weight:800; text-transform:uppercase; letter-spacing:0.03em; color:var(--sec); margin:0 0 0.75rem; display:flex; align-items:center; gap:0.5rem; } .tp-legend { margin-left:auto; font-weight:600; text-transform:none; letter-spacing:0; color:var(--mut); display:inline-flex; align-items:center; gap:0.3rem; } .tp-dot { width:0.6rem; height:0.6rem; border-radius:50%; display:inline-block; margin-left:0.5rem; }
	.tp-chart { display:flex; align-items:flex-end; gap:0.5rem; height:11rem; }
	.tp-bar { flex:1; display:flex; flex-direction:column; align-items:center; justify-content:flex-end; gap:0.3rem; height:100%; } .tp-bar-pair { display:flex; align-items:flex-end; gap:2px; height:100%; width:100%; justify-content:center; }
	.tp-bar-c { width:38%; max-width:1.1rem; min-height:2px; border-radius:3px 3px 0 0; position:relative; display:flex; justify-content:center; } .tp-bar-n { position:absolute; top:-1rem; font-size:0.6rem; font-weight:800; color:var(--sec); } .tp-bar-x { font-size:0.62rem; color:var(--mut); font-weight:700; }
	.tp-dimtable { width:100%; border-collapse:collapse; font-size:0.85rem; } .tp-dimtable th { text-align:left; font-size:0.62rem; text-transform:uppercase; letter-spacing:0.03em; color:var(--mut); font-weight:800; padding:0 0.4rem 0.4rem; } .tp-dimtable td { padding:0.35rem 0.4rem; border-top:1px solid var(--b); } .tp-dim-n { font-weight:600; } .tp-dim-bar { width:45%; } .tp-dimbar { height:0.55rem; background:var(--bg2); border-radius:4px; overflow:hidden; } .tp-dimbar span { display:block; height:100%; } .tp-dim-v { text-align:right; font-weight:700; }
	.tp-empty { padding:2.5rem; text-align:center; color:var(--mut); border:1px dashed var(--b); border-radius:12px; } .tp-err { color:#b23b32; } .tp-empty-sm { color:var(--mut); font-size:0.85rem; padding:1.4rem; text-align:center; } .tp-note { font-size:0.8rem; color:var(--mut); }
</style>`;

module.exports = DocumentThroughputProvider;
module.exports.default_configuration = { ProviderIdentifier: 'DocumentThroughput' };
